import { getEphemeralData } from './storage';

export class ApiError extends Error {
  readonly headers: Headers;

  readonly url: string;

  readonly status: number;

  readonly statusText: string;

  readonly data: any;

  constructor(response: Omit<Response, 'ok'>, data: any) {
    super(response.statusText);
    Object.setPrototypeOf(this, new.target.prototype);

    this.headers = response.headers;
    this.url = response.url;
    this.status = response.status;
    this.statusText = response.statusText;
    this.data = data;
  }

  static async initialize(response: Response) {
    const contentType = response.headers?.get('content-type');
    if (response.status === 204) {
      return new ApiError(response, undefined);
    }
    if (contentType && contentType.indexOf('application/json') !== -1) {
      const jsonData = await response.json();
      return new ApiError(response, jsonData);
    }
    const text = await response.text();
    try {
      return new ApiError(response, JSON.parse(text));
    } catch {
      return new ApiError(response, text);
    }
  }
}

export function getRequestBody(data: any) {
  if (data === null || data === undefined) {
    return '';
  }
  if (typeof data === 'string') {
    return data;
  }
  return JSON.stringify(data);
}

export const X_AT_KEY = process.env.NEXT_PUBLIC_API_KEY;

export async function api<T>(url: string, init?: RequestInit): Promise<T> {
  // This works in the monolith where Rails has built-in CSRF handling. The token will be in a meta tag in server-rendered pages and we can then pluck the token use in fetch requests.
  const token = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
  // This mechanism allows default headers to be set by Next.js clients using this package. We can expand on this concept if needed for more customization.
  const defaultHeaders = { ...getEphemeralData('shared.api.fetch.headers') };

  const headers = {
    // an empty body is invalid content-type application/json
    ...(init?.body && { 'content-type': 'application/json' }),
    'X-AT-KEY': X_AT_KEY,
    'X-CSRF-TOKEN': token?.content,
    // eslint-disable-next-line no-underscore-dangle
    'X-Language-Locale': global?.__AT_DATA__?.languageRegionCode || 'en-US'
  };

  const response = await fetch(url, {
    credentials: process.env.NODE_ENV === 'development' ? 'include' : undefined,
    ...init,
    headers: {
      // if we're concerned about casing we could use Headers() interface
      ...headers,
      ...defaultHeaders,
      ...init?.headers
    }
  });

  if (!response.ok) {
    const apiError = await ApiError.initialize(response);
    throw apiError;
  }

  // Manually convert to json rather than using response.json because response.json throws an error on an empty response
  const json = await response.text().then(text => (text ? JSON.parse(text) : {}));

  return json;
}

export type Config = {
  params?: any;
  headers?: any;
};
